import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_HUTCH_APP, EnableHutchAppInput } from '../data/mutations'

/**
 * Saves Hutch app's api key.
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const HutchModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableHutchApp, { loading }] = useMutation<{ enableHutchApp: { apiKey: string } }, EnableHutchAppInput>(
    ENABLE_HUTCH_APP,
    {
      onCompleted: () => {
        showToast({
          status: 'success',
          title: `Hutch app is enabled successfully!`
        })
        refetchApps()
        onClose()
        setFormError('')
      },
      onError: (err) => {
        if (err.message === 'Incorrect API key') {
          setFormError('Invalid api key. Please try again.')
          showToast({
            status: 'error',
            title: `Invalid api key. Please try again.`
          })
        } else {
          setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
          showToast({
            status: 'error',
            title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
          })
        }
      }
    }
  )

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { apiKey: string; appUsername?: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableHutchApp({
      variables: { apiKey: event.apiKey, appType: appType }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/hutch"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an admin Hutch account. Enter your Hutch API key.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'Hutch API Key',
            name: 'apiKey',
            placeHolder: 'Enter your Hutch API Key',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

HutchModal.displayName = 'HutchModal'

export { HutchModal }
